import React, { useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { DetailData, UserAggregate, CDRTransaction } from "../../../../types/analytics";
import { formatNumber } from "./chartConfig";

interface UserStatsPanelProps {
  detailData: DetailData | null;
  loading: boolean;
}

const formatDate = (iso: string): string => {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleDateString("en", {
    day: "numeric",
    month: "short",
  });
};

const formatDuration = (start: string, end: string): string => {
  if (!start || !end) return "\u2014";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms <= 0) return "\u2014";
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const TransactionDetail: React.FC<{ transactions: CDRTransaction[] }> = ({
  transactions,
}) => {
  const sorted = [...transactions].sort((a, b) => {
    const da = a.startDateTime || "";
    const db = b.startDateTime || "";
    return db.localeCompare(da);
  });

  return (
    <DataTable value={sorted} size="small" className="ml-5">
      <Column
        header="Station"
        body={(tx: CDRTransaction) =>
          tx.chargingStationName || tx.evseId || "\u2014"
        }
      />
      <Column
        header="Date"
        body={(tx: CDRTransaction) => formatDate(tx.startDateTime)}
      />
      <Column
        header="kWh"
        body={(tx: CDRTransaction) =>
          formatNumber(Number(tx.totalEnergy) || 0)
        }
        align="right"
      />
      <Column
        header="Amount"
        body={(tx: CDRTransaction) =>
          tx.freeCharge
            ? "Free"
            : `${formatNumber(Number(tx.totalCost) || 0)} \u20AC`
        }
        align="right"
      />
      <Column
        header="Duration"
        body={(tx: CDRTransaction) =>
          formatDuration(tx.startDateTime, tx.endDateTime)
        }
        align="right"
      />
    </DataTable>
  );
};

const UserStatsPanel: React.FC<UserStatsPanelProps> = ({
  detailData,
  loading,
}) => {
  const [expandedRows, setExpandedRows] = useState<any>(null);

  if (loading) {
    return (
      <Card className="shadow-2" title="User Stats">
        <div className="flex flex-column gap-3">
          <div className="grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="col-12 sm:col-4">
                <Skeleton width="100%" height="60px" />
              </div>
            ))}
          </div>
          <Skeleton width="100%" height="200px" />
        </div>
      </Card>
    );
  }

  if (!detailData || detailData.topUsers.length === 0) {
    return (
      <Card className="shadow-2" title="User Stats">
        <div className="flex flex-column align-items-center justify-content-center text-center p-5">
          <i className="pi pi-users text-4xl text-300 mb-3" />
          <p className="text-500">No user data available</p>
        </div>
      </Card>
    );
  }

  const totalSessions = detailData.paidSessions + detailData.freeSessions;

  const allowExpansion = (row: UserAggregate) => row.isReturning;

  const rowExpansionTemplate = (data: UserAggregate) => (
    <TransactionDetail transactions={data.transactions} />
  );

  return (
    <Card className="shadow-2" title="User Stats">
      {/* Summary row */}
      <div className="grid mb-3">
        <div className="col-12 sm:col-4">
          <div className="surface-100 border-round p-3 text-center">
            <div className="text-500 text-sm mb-1">Unique Users</div>
            <div className="text-xl font-bold">{detailData.uniqueUsers}</div>
          </div>
        </div>
        <div className="col-12 sm:col-4">
          <div className="surface-100 border-round p-3 text-center">
            <div className="text-500 text-sm mb-1">Returning Users</div>
            <div className="text-xl font-bold">{detailData.returningUsers}</div>
          </div>
        </div>
        {totalSessions > 0 && (
          <div className="col-12 sm:col-4">
            <div className="surface-100 border-round p-3 text-center">
              <div className="text-500 text-sm mb-1">Paid Ratio</div>
              <div className="text-xl font-bold">
                {((detailData.paidSessions / totalSessions) * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Top users table */}
      <h4 className="text-lg font-medium mb-2">Top Users</h4>
      <DataTable
        value={detailData.topUsers}
        size="small"
        stripedRows
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="userId"
        sortField="sessions"
        sortOrder={-1}
      >
        <Column expander={allowExpansion} style={{ width: "3rem" }} />
        <Column field="displayId" header="User" sortable />
        <Column field="provider" header="Provider" sortable />
        <Column field="sessions" header="Sessions" sortable align="right" />
        <Column
          field="energy"
          header="kWh"
          body={(row) => formatNumber(row.energy)}
          sortable
          align="right"
        />
        <Column
          field="revenue"
          header="Revenue"
          body={(row) => `${formatNumber(row.revenue)} \u20AC`}
          sortable
          align="right"
        />
        <Column
          field="lastSession"
          header="Last Seen"
          body={(row) => formatDate(row.lastSession)}
          sortable
          align="right"
        />
      </DataTable>
    </Card>
  );
};

export default UserStatsPanel;
